/**
 * Enum configuration service - 枚举配置服务
 *
 * 用于获取和管理可配置的枚举选项，支持前端下拉框、状态标签等场景。
 * 使用 localStorage + 内存双重缓存提升性能。
 */

import { get, post, put, del } from '../utils/request';

// ============ Types ============

export interface EnumOption {
  value: string;
  label: string;
  label_en?: string;
  color?: string;
  icon?: string;
  description?: string;
  extra_data?: Record<string, any>;
}

export interface EnumOptionsResponse {
  category_code: string;
  category_name: string;
  module: string;
  options: EnumOption[];
}

export interface EnumValueCreate {
  value_code: string;
  label: string;
  label_en?: string;
  color?: string;
  description?: string;
  icon?: string;
  sort_order?: number;
  is_active?: boolean;
  parent_id?: number;
  extra_data?: Record<string, any>;
}

export interface EnumValueUpdate {
  label?: string;
  label_en?: string;
  color?: string;
  description?: string;
  icon?: string;
  sort_order?: number;
  is_active?: boolean;
  parent_id?: number;
  extra_data?: Record<string, any>;
}

export interface EnumValueResponse {
  id: number;
  category_id: number;
  value_code: string;
  label: string;
  label_en?: string;
  color?: string;
  description?: string;
  icon?: string;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  parent_id?: number;
  extra_data?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface EnumCategoryResponse {
  id: number;
  category_code: string;
  category_name: string;
  module: string;
  description?: string;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  values?: EnumValueResponse[];
}

// ============ Cache ============

// 内存缓存
const memoryCache: Record<string, { data: EnumOption[]; timestamp: number }> = {};

// 缓存过期时间（30分钟）
const CACHE_DURATION = 30 * 60 * 1000;

// 缓存键前缀
const CACHE_KEY_PREFIX = 'enum_cache_';

/**
 * 从缓存获取枚举选项
 */
function getFromCache(categoryCode: string): EnumOption[] | null {
  // 优先检查内存缓存
  const memoryCached = memoryCache[categoryCode];
  if (memoryCached && Date.now() - memoryCached.timestamp < CACHE_DURATION) {
    return memoryCached.data;
  }

  // 检查 localStorage 缓存
  try {
    const localCached = localStorage.getItem(`${CACHE_KEY_PREFIX}${categoryCode}`);
    if (localCached) {
      const parsed = JSON.parse(localCached);
      if (Date.now() - parsed.timestamp < CACHE_DURATION) {
        // 同步到内存缓存
        memoryCache[categoryCode] = parsed;
        return parsed.data;
      }
    }
  } catch (e) {
    console.warn('读取枚举缓存失败:', e);
  }

  return null;
}

/**
 * 存入缓存
 */
function setToCache(categoryCode: string, data: EnumOption[]): void {
  const cacheEntry = { data, timestamp: Date.now() };

  // 存入内存缓存
  memoryCache[categoryCode] = cacheEntry;

  // 存入 localStorage
  try {
    localStorage.setItem(`${CACHE_KEY_PREFIX}${categoryCode}`, JSON.stringify(cacheEntry));
  } catch (e) {
    console.warn('存储枚举缓存失败:', e);
  }
}

/**
 * 清除缓存
 */
export function invalidateEnumCache(categoryCode?: string): void {
  if (categoryCode) {
    delete memoryCache[categoryCode];
    try {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${categoryCode}`);
    } catch (e) {
      console.warn('清除枚举缓存失败:', e);
    }
  } else {
    // 清除所有缓存
    Object.keys(memoryCache).forEach(key => delete memoryCache[key]);
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(CACHE_KEY_PREFIX))
        .forEach(key => localStorage.removeItem(key));
    } catch (e) {
      console.warn('清除枚举缓存失败:', e);
    }
  }
}

// ============ Public API (获取枚举选项) ============

/**
 * 获取单个枚举分类的选项列表
 * 自动使用缓存提升性能
 */
export async function getEnumOptions(categoryCode: string): Promise<EnumOption[]> {
  // 尝试从缓存获取
  const cached = getFromCache(categoryCode);
  if (cached) {
    return cached;
  }

  // 从 API 获取
  const response = await get<EnumOptionsResponse>(`/enums/options/${categoryCode}`);

  // 存入缓存
  if (response && response.options) {
    setToCache(categoryCode, response.options);
    return response.options;
  }

  return [];
}

/**
 * 获取所有枚举选项（可按模块过滤）
 */
export async function getAllEnumOptions(module?: string): Promise<EnumOptionsResponse[]> {
  const response = await get<EnumOptionsResponse[]>('/enums/options', { params: { module } });
  return response || [];
}

/**
 * 获取模块列表
 */
export async function getEnumModules(): Promise<string[]> {
  const response = await get<string[]>('/enums/modules');
  return response || [];
}

/**
 * 根据值获取标签
 */
export async function getEnumLabel(categoryCode: string, value: string): Promise<string> {
  const options = await getEnumOptions(categoryCode);
  const option = options.find(o => o.value === value);
  return option?.label || value;
}

/**
 * 根据值获取颜色
 */
export async function getEnumColor(categoryCode: string, value: string): Promise<string> {
  const options = await getEnumOptions(categoryCode);
  const option = options.find(o => o.value === value);
  return option?.color || 'default';
}

// ============ Admin API (枚举管理) ============

/**
 * 列出所有枚举分类（管理员）
 */
export async function listEnumCategories(
  module?: string,
  includeValues = true
): Promise<EnumCategoryResponse[]> {
  const response = await get<EnumCategoryResponse[]>('/enums/categories', {
    params: {
      module,
      include_values: includeValues,
    },
  });
  return response || [];
}

/**
 * 获取单个分类详情（管理员）
 */
export async function getEnumCategory(categoryId: number): Promise<EnumCategoryResponse> {
  return get<EnumCategoryResponse>(`/enums/categories/${categoryId}`);
}

/**
 * 创建枚举分类（管理员）
 */
export async function createEnumCategory(
  data: { category_code: string; category_name: string; module: string; description?: string }
): Promise<EnumCategoryResponse> {
  const response = await post<EnumCategoryResponse>('/enums/categories', data);
  invalidateEnumCache();
  return response;
}

/**
 * 更新枚举分类（管理员）
 */
export async function updateEnumCategory(
  categoryId: number,
  data: { category_name?: string; description?: string; is_active?: boolean }
): Promise<EnumCategoryResponse> {
  const response = await put<EnumCategoryResponse>(`/enums/categories/${categoryId}`, data);
  invalidateEnumCache();
  return response;
}

/**
 * 删除枚举分类（管理员）
 */
export async function deleteEnumCategory(categoryId: number): Promise<void> {
  await del(`/enums/categories/${categoryId}`);
  invalidateEnumCache();
}

/**
 * 列出分类下的枚举值（管理员）
 */
export async function listEnumValues(
  categoryId: number,
  includeInactive = false
): Promise<EnumValueResponse[]> {
  const response = await get<EnumValueResponse[]>(`/enums/categories/${categoryId}/values`, {
    params: {
      include_inactive: includeInactive,
    },
  });
  return response || [];
}

/**
 * 创建枚举值（管理员）
 */
export async function createEnumValue(
  categoryId: number,
  data: EnumValueCreate
): Promise<EnumValueResponse> {
  const response = await post<EnumValueResponse>(`/enums/categories/${categoryId}/values`, data);
  invalidateEnumCache();
  return response;
}

/**
 * 更新枚举值（管理员）
 */
export async function updateEnumValue(
  valueId: number,
  data: EnumValueUpdate
): Promise<EnumValueResponse> {
  const response = await put<EnumValueResponse>(`/enums/values/${valueId}`, data);
  invalidateEnumCache();
  return response;
}

/**
 * 删除枚举值（管理员）
 */
export async function deleteEnumValue(valueId: number): Promise<void> {
  await del(`/enums/values/${valueId}`);
  invalidateEnumCache();
}

/**
 * 批量导入枚举值（管理员）
 */
export async function batchImportEnumValues(
  categoryCode: string,
  values: EnumValueCreate[],
  replaceExisting = false
): Promise<EnumValueResponse[]> {
  const response = await post<EnumValueResponse[]>('/enums/batch-import', {
    category_code: categoryCode,
    values,
    replace_existing: replaceExisting,
  });
  invalidateEnumCache(categoryCode);
  return response || [];
}

/**
 * 刷新缓存（管理员）
 */
export async function refreshEnumCache(categoryCode?: string): Promise<void> {
  await post('/enums/refresh-cache', { category_code: categoryCode });
  invalidateEnumCache(categoryCode);
}

// ============ Helper Functions ============

/**
 * 将枚举选项转换为 Ant Design Select options 格式
 */
export function toSelectOptions(options: EnumOption[]): Array<{ value: string; label: string }> {
  return options.map(opt => ({
    value: opt.value,
    label: opt.label,
  }));
}

/**
 * 将枚举选项转换为 Ant Design valueEnum 格式
 */
export function toValueEnum(options: EnumOption[]): Record<string, { text: string; status?: string }> {
  const result: Record<string, { text: string; status?: string }> = {};
  options.forEach(opt => {
    result[opt.value] = {
      text: opt.label,
      status: opt.color,
    };
  });
  return result;
}

/**
 * 获取选项的标签映射
 */
export function getLabelMap(options: EnumOption[]): Record<string, string> {
  const result: Record<string, string> = {};
  options.forEach(opt => {
    result[opt.value] = opt.label;
  });
  return result;
}

/**
 * 获取选项的颜色映射
 */
export function getColorMap(options: EnumOption[]): Record<string, string> {
  const result: Record<string, string> = {};
  options.forEach(opt => {
    if (opt.color) {
      result[opt.value] = opt.color;
    }
  });
  return result;
}